import { COOKIE_CONSENT_COOKIE, hasFunctionalCookieConsent } from "shared/CookieConsent";
import { FIRST_PARTY_COOKIES } from "shared/SharedConstants";

export const DOCS_LANGUAGES = [
  "ts-sdk",
  "ts-app-sdk",
  "js-sdk",
  "js-app-sdk",
  "curl",
  "fetch",
  "python",
  "go",
  "rust",
  "ruby",
  "java",
  "php",
  "python-http",
  "go-http",
] as const;
export type DocsLanguage = (typeof DOCS_LANGUAGES)[number];

export const parseDocsLanguage = (value: string | null | undefined): DocsLanguage =>
  DOCS_LANGUAGES.includes(value as DocsLanguage) ? (value as DocsLanguage) : "ts-sdk";

/** Pages whose SDK examples render in the saved language on the server: the docs and the blog. */
export const rendersSavedDocsLanguage = (pathname: string) =>
  ["/docs", "/blog"].some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

/**
 * The language docs and blog pages render on the server: the saved choice, which is only kept with
 * functional cookie consent. The edge cache keys those pages on the same value (src/lib/CFCache.ts).
 */
export const getSavedDocsLanguage = (getCookie: (name: string) => string | undefined) =>
  parseDocsLanguage(
    hasFunctionalCookieConsent(getCookie(COOKIE_CONSENT_COOKIE.name))
      ? getCookie(FIRST_PARTY_COOKIES.DOCS_LANGUAGE.name)
      : undefined,
  );

/** SDKs with their own reference pages under `/docs/sdk/<language>`. */
export const SDK_LANGUAGES = ["javascript", "python", "go", "rust", "ruby", "java", "php"] as const;
export type SdkLanguage = (typeof SDK_LANGUAGES)[number];

export const parseSdkLanguage = (value: string | null | undefined): SdkLanguage | null =>
  SDK_LANGUAGES.includes(value as SdkLanguage) ? (value as SdkLanguage) : null;

/** The SDK a docs page documents, from its path (e.g. `sdk/python/testing`), if any. */
export const getDocsPathSdkLanguage = (path: string): SdkLanguage | undefined =>
  parseSdkLanguage(/^\/?(?:docs\/)?sdk\/([^/#]+)/.exec(path)?.[1]) ?? undefined;

/** The highlighting language for code shown under a docs language tab. */
export const getDocsLanguageSyntax = (language: DocsLanguage): string =>
  language === "ts-sdk" || language === "ts-app-sdk"
    ? "typescript"
    : language === "js-sdk" || language === "js-app-sdk" || language === "fetch"
      ? "javascript"
      : language === "curl"
        ? "bash"
        : language === "python-http"
          ? "python"
          : language === "go-http"
            ? "go"
            : language;

/** SDK guide examples highlight like their tab, except install commands (shell, or Maven XML). */
export const getSdkExampleSyntax = (example: string, language: DocsLanguage): string =>
  example === "install" ? (language === "java" ? "xml" : "bash") : getDocsLanguageSyntax(language);

/**
 * Where a selection goes when an example lacks the selected language: SDK guide examples fall back
 * to the SDK in the same language, request previews to plain HTTP, inline calls to cURL. Anything
 * without a fallback shows the example's first tab.
 */
export const DOCS_LANGUAGE_FALLBACKS = {
  sdk: {
    "ts-sdk": "ts-app-sdk",
    "ts-app-sdk": "ts-sdk",
    "js-sdk": "js-app-sdk",
    "js-app-sdk": "js-sdk",
    "python-http": "python",
    "go-http": "go",
  },
  request: {
    rust: "curl",
    ruby: "curl",
    java: "curl",
    php: "curl",
    "js-sdk": "fetch",
    "ts-sdk": "fetch",
    "js-app-sdk": "fetch",
    "ts-app-sdk": "fetch",
    python: "python-http",
    go: "go-http",
  },
  inline: {
    "ts-sdk": "js-sdk",
    "ts-app-sdk": "js-sdk",
    "js-app-sdk": "js-sdk",
    fetch: "curl",
    "python-http": "curl",
    "go-http": "curl",
  },
} as const satisfies Record<string, Partial<Record<DocsLanguage, DocsLanguage>>>;

export type DocsLanguageFallback = keyof typeof DOCS_LANGUAGE_FALLBACKS;

/** The tab an example shows for the selected language, given the languages it offers. */
export const resolveDocsLanguage = (
  language: DocsLanguage,
  available: readonly DocsLanguage[],
  fallback: DocsLanguageFallback,
): DocsLanguage => {
  if (available.includes(language)) return language;
  const next = (DOCS_LANGUAGE_FALLBACKS[fallback] as Partial<Record<DocsLanguage, DocsLanguage>>)[
    language
  ];
  return next && available.includes(next) ? next : available[0];
};

/** JavaScript tabs and the TypeScript tab that shows the same SDK. */
const TYPESCRIPT_TWINS: Partial<Record<DocsLanguage, DocsLanguage>> = {
  "js-sdk": "ts-sdk",
  "js-app-sdk": "ts-app-sdk",
};

/**
 * Drops each JavaScript tab whose code is the same as its TypeScript twin's, so one "JS/TS" tab
 * shows both. Returns the remaining tabs and the TypeScript tabs that now stand for both.
 */
export const mergeIdenticalScriptTabs = (
  tabs: readonly DocsLanguage[],
  sameCode: (javascript: DocsLanguage, typescript: DocsLanguage) => boolean,
) => {
  const merged = new Set<DocsLanguage>();
  const kept = tabs.filter((tab) => {
    const twin = TYPESCRIPT_TWINS[tab];
    if (!twin || !tabs.includes(twin) || !sameCode(tab, twin)) return true;
    merged.add(twin);
    return false;
  });
  return { tabs: kept, merged };
};

/** The tab a reader's language selects when its JavaScript tab was merged into its twin. */
export const getMergedDocsLanguage = (
  language: DocsLanguage,
  merged: ReadonlySet<DocsLanguage>,
): DocsLanguage => {
  const twin = TYPESCRIPT_TWINS[language];
  return twin && merged.has(twin) ? twin : language;
};
